import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { USER_PROFILE } from '@constants/common/statusTypes';
import { commonModalTypes } from '@constants/common/modals/commonModalTypes';
import { configs } from '@constants/configs/configs';
import { showModal } from '@actions/common/actionCreators';
import { api } from '@modules/common-utils-services/axiosUtil';

export interface IUseKeyCloakStatusParams {
    encryptedUserId: string | undefined;
    shouldFetch: boolean;
    useKeyCloakForDisplay: boolean;
    userStatus: string | null | undefined;
    idmUserStatus: string | null | undefined;
}

const fetchKeyCloakStatus = async (encryptedUserId: string) => {
    const url = configs.endpoints.KEYCLOAK_STATUS.replace('${encryptedUserId}', encryptedUserId);
    const response = await api.get(url);
    return response.data;
};

const mapToUserProfileStatus = (status: string): string => {
    const upper = status.toUpperCase();
    return (
        Object.values(USER_PROFILE).find((p) => p.toUpperCase() === upper) ?? status
    );
};

const useKeyCloakStatus = ({
    encryptedUserId,
    shouldFetch,
    useKeyCloakForDisplay,
    userStatus,
    idmUserStatus,
}: IUseKeyCloakStatusParams) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const [keycloakStatus, setKeycloakStatus] = useState<string | null>(null);

    useEffect(() => {
        const getKeyCloakStatusHandler = async () => {
            if (!encryptedUserId || !shouldFetch) return;

            try {
                const response = await fetchKeyCloakStatus(encryptedUserId);
                if (response?.status) {
                    const mappedStatus = mapToUserProfileStatus(response.status);
                    setKeycloakStatus(mappedStatus);
                }
            } catch (error) {
                const errorMessage = intl.formatMessage({ id: 'common.technical_issues_body' });
                dispatch(
                    showModal({
                        modalType: commonModalTypes.REQUEST_ERROR,
                        modalData: { message: errorMessage },
                    }),
                );
            }
        };
        getKeyCloakStatusHandler();
    }, [encryptedUserId, shouldFetch, dispatch, intl]);

    const shouldUseKeycloakStatus = useKeyCloakForDisplay && keycloakStatus;
    const effectiveUserStatus =
        (shouldUseKeycloakStatus ? keycloakStatus : userStatus) ?? undefined;
    const effectiveIdmUserStatus =
        (shouldUseKeycloakStatus && keycloakStatus === USER_PROFILE.LOCKED
            ? USER_PROFILE.LOCKED
            : idmUserStatus) ?? undefined;

    return { keycloakStatus, effectiveUserStatus, effectiveIdmUserStatus };
};

export default useKeyCloakStatus;
